import { Typography } from "@mui/material";
import { differenceInDays } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OrderStatus } from "src/api/types";
import { useLatestOrder } from "src/context/LatestOrder";
import { usePatient } from "src/context/PatientProvider";

const CelebrationMessage = () => {
  const { t } = useTranslation();
  const component = "patient-dashboard.celebration-message";
  const status = useLatestOrder((o) => o.status);
  const deliveredAt = useLatestOrder((o) => o.delivered_at);
  const firstName = usePatient((p) => p.profile.first_name);

  const tenDaysSinceDelivered = useMemo(() => {
    if (deliveredAt == null) return false;

    const daysDifference = differenceInDays(new Date(), new Date(deliveredAt));

    return status === "delivered" && daysDifference > 10;
  }, [deliveredAt, status]);

  const message = useMemo(() => {
    if (status == null) return null;

    if (["sent_to_pharmacy", "shipped"].includes(status)) {
      return t(
        `${component}.${OrderStatus.SentToPharmacy}`,
        "Hang tight! 🎉 Your order is being prepared and will be on its way soon. This is the first step in your journey toward feeling healthier, stronger, and more energized. We’ll keep you updated every step of the way—your tools for success are almost here!"
      );
    }

    if (tenDaysSinceDelivered) {
      return t(
        `${component}.${OrderStatus.Delivered}-10-days`,
        "We hope you’re settling in well with your new treatment, {{name}}! 🎉 By now, you’ve had a chance to get started and begin building healthy routines. Remember, progress comes from consistency—every choice you make brings you closer to your goals. Keep it up, and know we’re here to support you along the way.",
        { name: firstName }
      );
    }

    if (status === "delivered") {
      return t(
        `${component}.${OrderStatus.Delivered}`,
        "Great news, {{name}}! Your order has arrived and you’re officially ready to begin your transformation. With your GLP-1 treatment now in hand, plus your personalized fitness and nutrition plans, you’ve got everything you need to take the next big step toward your goals. Let’s celebrate this milestone and keep the momentum going!",
        { name: firstName }
      );
    }

    return null;
  }, [firstName, status, t, tenDaysSinceDelivered]);

  if (message == null) return null;

  return (
    <Typography variant="h6" letterSpacing={0.5}>
      {message}
    </Typography>
  );
};

export default CelebrationMessage;
